import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { Box, Button, Card, Stack } from '@mui/material';
import { captureException } from '@sentry/react';
import { useContext, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useSWRConfig } from 'swr';

import { SectionTitle } from '~/components/common/SectionTitle';
import { ComplianceDocuments } from '~/components/compliance/ComplianceDocuments';
import { ComplianceDocumentUpload } from '~/components/compliance/ComplianceDocumentUpload';
import { ElevatorComplianceFields } from '~/components/compliance/detailPage/ElevatorComplianceFields';
import { ElevatorViolationsCard } from '~/components/compliance/detailPage/ElevatorViolationsCard';
import {
  getDefaultViolationValues,
  getEditViolationValues,
} from '~/components/compliance/violationFormValues';
import { useViolations } from '~/components/compliance/violationsFetcher';
import AddViolationDialog from '~/components/uploadDocument/AddViolationDialog';
import { UploadDocumentDialogFlowContext } from '~/components/uploadDocument/UploadDocumentDialogFlow';
import type { ViolationFormData } from '~/components/uploadDocument/violationForm';
import { getCertificateMutator } from '~/data/hooks/utils';
import type { API_Compliance } from '~/data/schemas/compliance';
import useLAISnackbar, { ActionEnum } from '~/hooks/useLAISnackbar';
import { BUTTON_SX } from '~/theme';

type AddViolationDialogState =
  | { open: false }
  | {
      open: true;
      propertyId: string;
      carId: string;
      editViolationId?: string;
      defaultValues: ViolationFormData;
    };

/**
 * The Compliance tab: the certificate, the car's violations, and the documents filed
 * against it — three cards down the page, the shape a contract's tabs use. The drawer
 * had all of it in 500px, as one column of half-width fields over an untitled list.
 */
export function ElevatorComplianceTab({
  compliance,
  onCertificateAdded,
}: {
  compliance: API_Compliance;
  /** Re-reads the compliance record after a certificate is added. */
  onCertificateAdded: () => void;
}) {
  const [, setSearchParams] = useSearchParams();
  const [violationDialogState, setViolationDialogState] = useState<AddViolationDialogState>({
    open: false,
  });

  const { violations, isLoading, refresh: refreshViolations } = useViolations(compliance.carId);
  const { start } = useContext(UploadDocumentDialogFlowContext);
  const { showEntityActionSnackbar } = useLAISnackbar();
  const { mutate } = useSWRConfig();

  const handleViolationSubmit = async () => {
    try {
      await mutate(getCertificateMutator(compliance.certificateId));
    } catch (error) {
      captureException(error);
      showEntityActionSnackbar(
        { name: 'Violation', action: ActionEnum.Fail, id: undefined },
        { variant: 'error' },
      );
    } finally {
      setViolationDialogState({ open: false });
    }
  };

  // The upload dialog is mounted app-wide and told which car it is for through
  // the URL, exactly as the drawer's certificate link did.
  const openCertificateUpload = () => {
    start();
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('propertyId', compliance.propertyId);
      next.set('carId', compliance.carId);
      return next;
    });
  };

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <Stack gap={3}>
        <Card variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <SectionTitle>Certificate</SectionTitle>
            <Button
              size="small"
              startIcon={<FileDownloadOutlinedIcon />}
              disabled={!compliance.downloadUrl}
              onClick={() => {
                if (compliance.downloadUrl) {
                  window.open(compliance.downloadUrl, '_blank', 'noopener');
                }
              }}
              sx={BUTTON_SX}
            >
              {compliance.downloadUrl ? 'Download' : 'None on file'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<UploadFileOutlinedIcon />}
              onClick={openCertificateUpload}
              sx={BUTTON_SX}
            >
              {compliance.downloadUrl ? 'Upload new' : 'Upload'}
            </Button>
          </Stack>
          <Box mt={2}>
            <ElevatorComplianceFields compliance={compliance} />
          </Box>
        </Card>

        <ElevatorViolationsCard
          compliance={compliance}
          violations={violations}
          isLoading={isLoading}
          onAddViolation={() =>
            setViolationDialogState({
              open: true,
              propertyId: compliance.propertyId,
              carId: compliance.carId,
              defaultValues: getDefaultViolationValues(compliance),
            })
          }
          onEditViolation={(violationId) => {
            const violation = violations.find((v) => v.id === violationId);
            if (!violation) return;

            setViolationDialogState({
              open: true,
              propertyId: compliance.propertyId,
              carId: compliance.carId,
              editViolationId: violationId,
              defaultValues: getEditViolationValues(compliance, violation),
            });
          }}
          onNoteAdded={() => void refreshViolations()}
        />

        <Card variant="outlined" sx={{ p: 2 }}>
          <SectionTitle>Documents</SectionTitle>
          <Box mt={2} mb={2}>
            <ComplianceDocumentUpload
              onCertificateAdded={onCertificateAdded}
              propertyId={compliance.propertyId}
              carId={compliance.carId}
            />
          </Box>
          <ComplianceDocuments carId={compliance.carId} />
        </Card>
      </Stack>

      <AddViolationDialog
        open={violationDialogState.open}
        onClose={() => setViolationDialogState({ open: false })}
        onSubmit={handleViolationSubmit}
        isEdit={violationDialogState.open ? !!violationDialogState.editViolationId : false}
        violationId={violationDialogState.open ? violationDialogState.editViolationId : undefined}
        defaultValues={violationDialogState.open ? violationDialogState.defaultValues : undefined}
      />
    </Box>
  );
}
